if (!customElements.get('pump-upsell')) {
  customElements.define('pump-upsell', class PumpUpsell extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.formId = this.dataset.productFormId;
      this.blockId = this.dataset.blockId;
      this.pumpProductId = Number(this.dataset.pumpProductId);
      this.isUpsellMode = this.dataset.pumpMode === 'upsell';
      this.hasVoltageOptions = !!this.querySelector('.pump-voltage-section');
      this.hasSingleVariant = !!this.querySelector(`input[type="hidden"][name="voltage-${this.blockId}"]`);
      this.selectorBox = this.querySelector('.pump-selector');
      this.voltageSection = this.querySelector('.pump-voltage-section');
      this.pumpError = this.querySelector('[data-pump-error]');
      this.voltageError = this.querySelector('[data-voltage-error]');
      this.pumpRadios = this.querySelectorAll(`input[name="pump-${this.blockId}"]`);
      this.voltageRadios = this.querySelectorAll(`input[name="voltage-${this.blockId}"]`);

      // Pump selection
      this.pumpRadios.forEach(radio => {
        radio.addEventListener('change', this.onPumpChange.bind(this));
      });

      // Voltage selection
      this.voltageRadios.forEach(radio => {
        radio.addEventListener('change', this.onVoltageChange.bind(this));
      });

      // Listen for cart changes (item removed/updated in cart drawer)
      this._onCartChange = this.onCartChange.bind(this);
      document.addEventListener('cart:change', this._onCartChange);

      // The buy_buttons block renders after this block, so the submit button
      // doesn't exist yet. Wait for the full page to be parsed before binding.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initFormBinding());
      } else {
        this.initFormBinding();
      }
    }

    disconnectedCallback() {
      if (this._onCartChange) {
        document.removeEventListener('cart:change', this._onCartChange);
      }
    }

    initFormBinding() {
      this.submitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      this.productForm = document.getElementById(this.formId);

      if (!this.submitButton) {
        // Fallback: the form might be rendered by a deferred script, poll briefly
        const poll = setInterval(() => {
          this.submitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.productForm = document.getElementById(this.formId);
          if (this.submitButton) {
            clearInterval(poll);
            this.bindSubmitButton();
          }
        }, 100);
        // Stop polling after 5s
        setTimeout(() => clearInterval(poll), 5000);
        return;
      }

      this.bindSubmitButton();
    }

    bindSubmitButton() {
      this.submitting = false;

      // If a pump option is already pre-selected (e.g., pump in cart), enable ATC
      if (this.isValid) {
        this.enableATC();
      } else {
        this.disableATC();
      }

      // Intercept submit button click (fires before form submit event)
      this.submitButton.addEventListener('click', this.onSubmitClick.bind(this));

      // Intercept form submit for Enter key submissions.
      // Must listen on <product-form> parent in CAPTURE phase so we fire
      // before product-form.js's handler on the <form> element.
      const productFormWrapper = this.productForm.closest('product-form');
      if (productFormWrapper) {
        productFormWrapper.addEventListener('submit', this.onFormSubmit.bind(this), true);
      }
    }

    get pumpSelection() {
      const checked = this.querySelector(`input[name="pump-${this.blockId}"]:checked`);
      return checked ? checked.value : null;
    }

    get selectedVoltageVariantId() {
      if (this.hasSingleVariant) {
        return this.querySelector(`input[type="hidden"][name="voltage-${this.blockId}"]`).value;
      }
      const checked = this.querySelector(`input[name="voltage-${this.blockId}"]:checked`);
      return checked ? checked.value : null;
    }

    get needsPump() {
      return this.isUpsellMode ? this.pumpSelection === 'yes' : this.pumpSelection === 'no';
    }

    get isValid() {
      if (!this.pumpSelection) return false;
      if (this.needsPump && !this.selectedVoltageVariantId) return false;
      return true;
    }

    onPumpChange(_e) {
      this.selectorBox.classList.remove('has-error');
      if (this.pumpError) this.pumpError.classList.remove('visible');

      if (this.needsPump && this.voltageSection) {
        this.voltageSection.classList.add('open');
      } else if (this.voltageSection) {
        this.voltageSection.classList.remove('open');
        this.voltageRadios.forEach(v => v.checked = false);
        if (this.voltageError) this.voltageError.classList.remove('visible');
      }

      this.updateATCState();
    }

    onVoltageChange() {
      if (this.voltageError) this.voltageError.classList.remove('visible');
      this.updateATCState();
    }

    updateATCState() {
      if (this.isValid) {
        this.enableATC();
      } else {
        this.disableATC();
      }
    }

    disableATC() {
      if (!this.submitButton) return;
      this.submitButton.setAttribute('aria-disabled', 'true');
    }

    enableATC() {
      if (!this.submitButton) return;
      // Only enable if the variant itself is available (not sold out)
      const variantInput = this.productForm?.querySelector('input[name="id"]');
      if (variantInput && variantInput.disabled) return;
      this.submitButton.removeAttribute('aria-disabled');
    }

    onCartChange(e) {
      // Check if pump product is still in the cart
      const cartData = e.detail?.cart || e.detail;
      if (!cartData?.items) return;

      const pumpInCart = cartData.items.some(item => item.product_id === this.pumpProductId);

      if (pumpInCart) {
        this.showPumpInCartNotice();
      } else {
        this.hidePumpInCartNotice();
      }
    }

    showPumpInCartNotice() {
      if (this.querySelector('.pump-notice--in-cart')) return;

      const notice = document.createElement('div');
      notice.className = 'pump-notice pump-notice--in-cart';
      notice.innerHTML = '<span class="pump-notice-icon" aria-hidden="true">ℹ️</span>' +
        '<span>You already have a pump in your cart. You can skip this unless you need an additional one.</span>';

      this.selectorBox.parentNode.insertBefore(notice, this.selectorBox);

      const skipId = this.isUpsellMode ? `pump-no-${this.blockId}` : `pump-yes-${this.blockId}`;
      const skipRadio = this.querySelector(`#${skipId}`);
      if (skipRadio && !skipRadio.checked) {
        skipRadio.checked = true;
        skipRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    hidePumpInCartNotice() {
      const notice = this.querySelector('.pump-notice--in-cart');
      if (!notice) return;
      notice.remove();

      if (this.isUpsellMode) {
        return;
      }

      this.pumpRadios.forEach(r => r.checked = false);
      if (this.voltageSection) {
        this.voltageSection.classList.remove('open');
        this.voltageRadios.forEach(v => v.checked = false);
      }
      this.updateATCState();
    }

    showErrors() {
      if (!this.pumpSelection && this.pumpError) {
        this.selectorBox.classList.add('has-error');
        this.pumpError.classList.add('visible');
        // Re-trigger shake animation
        this.selectorBox.style.animation = 'none';
        this.selectorBox.offsetHeight;
        this.selectorBox.style.animation = '';
      }

      if (this.needsPump && !this.selectedVoltageVariantId && this.voltageError) {
        this.voltageError.classList.add('visible');
      }
    }

    onSubmitClick(e) {
      // Guard against double-click while already submitting
      if (this.submitting) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      // If pump upsell is not valid, block submission and show errors
      if (!this.isValid) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.showErrors();
        return;
      }

      // If user selected "Yes, I have one" — let default form submission proceed
      if (!this.needsPump) return;

      // User wants pump — intercept click, prevent form submit, do multi-item add
      e.preventDefault();
      e.stopImmediatePropagation();
      this.addBothToCart();
    }

    onFormSubmit(e) {
      // Catches Enter key submissions that bypass the click handler.
      // If pump needs to be added, we must intercept and do multi-item add.
      if (this.submitting) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }

      if (!this.isValid) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.showErrors();
        return;
      }

      if (this.needsPump) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.addBothToCart();
      }
      // "Yes" path — let product-form.js handle it
    }

    async addBothToCart() {
      if (this.submitting) return;
      this.submitting = true;

      const mainVariantId = this.productForm.querySelector('input[name="id"]').value;
      const quantityInput = this.productForm.querySelector('input[name="quantity"]');
      const mainQuantity = quantityInput ? Number(quantityInput.value) : 1;
      const pumpVariantId = this.selectedVoltageVariantId;

      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      // Get the product-form element for error message display
      const productFormEl = this.productForm.closest('product-form');

      try {
        const body = {
          items: [
            { id: Number(mainVariantId), quantity: mainQuantity },
            { id: Number(pumpVariantId), quantity: 1 }
          ]
        };

        if (cart) {
          body.sections = cart.getSectionsToRender().map(s => s.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        const response = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(body)
        });

        const data = await response.json();

        if (data.status) {
          // Show error to user via product-form's error wrapper
          if (productFormEl) {
            productFormEl.handleErrorMessage(data.description);
          }

          document.dispatchEvent(new CustomEvent('cart:error', {
            detail: {
              source: 'pump-upsell',
              productVariantId: mainVariantId,
              errors: data.description,
              message: data.message
            }
          }));
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'pump-upsell',
            productVariantId: mainVariantId,
            errors: data.description,
            message: data.message
          });
          return;
        }

        // Clear any previous error messages
        if (productFormEl) {
          productFormEl.handleErrorMessage();
        }

        // Update UI to reflect pump is now in cart
        this.showPumpInCartNotice();

        document.dispatchEvent(new CustomEvent('variant:add', {
          detail: {
            variant: { id: mainVariantId },
            quantity: mainQuantity,
            formElement: this.productForm,
            sectionId: this.sectionId
          }
        }));

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'pump-upsell',
          productVariantId: mainVariantId
        });

        document.dispatchEvent(new CustomEvent('cart:change', {
          detail: data,
          bubbles: true
        }));

        if (cart) {
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { cart.renderContents(data) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            cart.renderContents(data);
          }
        } else {
          window.location = window.routes.cart_url;
        }
      } catch (error) {
        console.error('Pump upsell cart add error:', error);
        if (productFormEl) {
          productFormEl.handleErrorMessage('Something went wrong. Please try again.');
        }
      } finally {
        this.submitting = false;
        this.submitButton.classList.remove('loading');
        if (this.isValid) {
          this.submitButton.removeAttribute('aria-disabled');
        }
      }
    }
  });
}
